// Triagem de um modelo IFC: estrutura espacial, unidades, que classes
// contém e se está georreferenciado.
//
// A contagem por classe depende de o host saber enumerar os tipos
// presentes no ficheiro, coisa que o contrato estreito não dá. Por isso
// entra aqui já apurada (`RawClassCount[]`) e o que é partilhado é a
// interpretação: o que conta como elemento físico e o que é estrutura
// espacial. Essa distinção é uma decisão, não um detalhe de leitura, e é
// justamente o tipo de coisa que não pode divergir entre consumidores.

import type { IfcReadSource, ModelFacts } from './source.js'
import { emptyGeoRef, readGeoRef, readUnits, type GeoRefInfo } from './geoRef.js'
import { num, str } from './values.js'

export interface ClassCount {
  ifcClass: string
  count: number
}

export interface StoreyInfo {
  name: string | null
  elevation: number | null
}

/** Contagem crua vinda do host, antes de se decidir o que é o quê. */
export interface RawClassCount {
  ifcClass: string
  count: number
  /** O que o parser diz sobre ser um IfcElement. */
  isElement: boolean
}

export interface IfcSummary {
  schema: string
  application: string | null
  projectName: string | null
  siteName: string | null
  buildingNames: string[]
  storeys: StoreyInfo[]
  units: Record<string, string>
  totalEntities: number | null
  elementClasses: ClassCount[]
  totalElements: number
  spatialClasses: ClassCount[]
  georef: GeoRefInfo
}

/** Classes que o parser classifica como elemento mas que um leitor
 *  humano não chamaria "elemento físico". A ordem importa na função
 *  abaixo: estas ganham à resposta do parser. */
export const SPATIAL_CLASSES: ReadonlySet<string> = new Set([
  'IFCSPACE',
  'IFCZONE',
  'IFCGRID',
  'IFCBUILDINGSTOREY',
  'IFCBUILDING',
  'IFCSITE',
  'IFCSPATIALZONE',
  'IFCANNOTATION',
  'IFCPROXY',
])

export function splitClassCounts(all: readonly RawClassCount[]): {
  elementClasses: ClassCount[]
  spatialClasses: ClassCount[]
  totalElements: number
} {
  const elementClasses: ClassCount[] = []
  const spatialClasses: ClassCount[] = []
  let totalElements = 0
  for (const t of all) {
    const name = t.ifcClass.toUpperCase()
    if (SPATIAL_CLASSES.has(name)) {
      spatialClasses.push({ ifcClass: name, count: t.count })
      continue
    }
    if (t.isElement) {
      totalElements += t.count
      elementClasses.push({ ifcClass: name, count: t.count })
    }
  }
  const byCount = (a: ClassCount, b: ClassCount) =>
    b.count - a.count || a.ifcClass.localeCompare(b.ifcClass)
  elementClasses.sort(byCount)
  spatialClasses.sort(byCount)
  return { elementClasses, spatialClasses, totalElements }
}

/** Projecto, local, edifícios e pisos, ordenados por cota. */
export function readSpatial(src: IfcReadSource): {
  projectName: string | null
  siteName: string | null
  buildingNames: string[]
  storeys: StoreyInfo[]
} {
  const storeys: StoreyInfo[] = []
  for (const id of src.getLineIDsByType('IFCBUILDINGSTOREY')) {
    const s = src.getEntity(id)
    if (!s) continue
    storeys.push({ name: str(s.Name), elevation: num(s.Elevation) })
  }
  storeys.sort((a, b) => (a.elevation ?? 0) - (b.elevation ?? 0))

  const buildingNames: string[] = []
  for (const id of src.getLineIDsByType('IFCBUILDING')) {
    const b = src.getEntity(id)
    if (b) buildingNames.push(str(b.Name) ?? '(sem nome)')
  }

  let projectName: string | null = null
  for (const id of src.getLineIDsByType('IFCPROJECT')) {
    const p = src.getEntity(id)
    if (p) {
      projectName = str(p.Name) ?? str(p.LongName)
      break
    }
  }

  let siteName: string | null = null
  for (const id of src.getLineIDsByType('IFCSITE')) {
    const s = src.getEntity(id)
    if (s) {
      siteName = str(s.Name) ?? str(s.LongName)
      break
    }
  }

  return { projectName, siteName, buildingNames, storeys }
}

/** Nunca lança por falta de dados: um campo ausente volta null e o
 *  relatório diz que não há, em vez de inventar. */
export function summarize(
  src: IfcReadSource,
  facts: ModelFacts = {},
  classes: readonly RawClassCount[] = [],
): IfcSummary {
  const spatial = readSpatial(src)
  const split = splitClassCounts(classes)
  let georef: GeoRefInfo
  try {
    georef = readGeoRef(src)
  } catch {
    georef = emptyGeoRef()
  }
  return {
    schema: facts.schema ?? 'desconhecido',
    application: facts.application ?? null,
    ...spatial,
    units: readUnits(src),
    totalEntities: facts.totalEntities ?? null,
    ...split,
    georef,
  }
}
